import type { ShopifyImportSource } from '../../types';
import { VariantGroupingRuleEditor } from './VariantGroupingRuleEditor';

interface Props {
  source: ShopifyImportSource;
  onChange: (updated: ShopifyImportSource) => void;
}

export function ShopifySourceEditor({ source, onChange }: Props) {
  const excludedText = source.excludedProductTypes.join('\n');

  return (
    <div className="space-y-3">
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">
          Store URL
        </label>
        <input
          type="url"
          value={source.storeUrl}
          onChange={(e) => onChange({ ...source, storeUrl: e.target.value })}
          placeholder="https://store.example.com"
          className="w-full text-sm border border-gray-300 rounded px-2 py-1"
        />
        <p className="text-[11px] text-gray-400 mt-1">
          The Shopify storefront origin. The scraper reads /products.json from
          this URL.
        </p>
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">
          Excluded product types
        </label>
        <textarea
          value={excludedText}
          onChange={(e) =>
            onChange({
              ...source,
              excludedProductTypes: e.target.value
                .split(/\n+/)
                .map((s) => s.trim())
                .filter(Boolean),
            })
          }
          placeholder="e.g. Straps&#10;Accessories"
          rows={3}
          className="w-full text-sm border border-gray-300 rounded px-2 py-1 font-mono"
        />
        <p className="text-[11px] text-gray-400 mt-1">
          One product type per line. Matching types are skipped during import.
        </p>
      </div>

      <div>
        <label className="flex items-start gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={source.ignoreParentChildRelationships ?? false}
            onChange={(e) =>
              onChange({ ...source, ignoreParentChildRelationships: e.target.checked })
            }
            className="mt-0.5 rounded border-gray-300"
          />
          <span>
            <span className="block text-xs font-medium text-gray-600">
              Ignore Shopify parent-child relationships
            </span>
            <span className="block text-[11px] text-gray-400">
              Import every variant as its own top-level reference instead of
              grouping variants under a shared parent. Use when a store's
              product→variant shape doesn't match how you want the refs stored.
            </span>
          </span>
        </label>
      </div>

      <VariantGroupingRuleEditor
        rule={source.variantGroupingRule}
        onChange={(variantGroupingRule) => onChange({ ...source, variantGroupingRule })}
      />
    </div>
  );
}
